import React, { useState, useEffect, useCallback } from "react";
import { NavLink, useLocation } from "react-router-dom";
import DashIcon from "components/icons/DashIcon";
import { MdArrowDropDown } from "react-icons/md";

export function SidebarLinks(props) {
  const { routes, isCollapsed } = props;
  const location = useLocation();
  const [expandedItems, setExpandedItems] = useState({});

  const activeRoute = useCallback(
    (routePath) =>
      location.pathname === routePath || location.pathname.includes(routePath),
    [location.pathname]
  );

  const isChildActive = useCallback(
    (children) => {
      return children?.some(
        (child) =>
          activeRoute(`${child.layout}/${child.path}`) ||
          (child.children && isChildActive(child.children))
      );
    },
    [activeRoute]
  );

  useEffect(() => {
    const newExpandedItems = {};
    routes.forEach((route, index) => {
      if (route.children && isChildActive(route.children)) {
        newExpandedItems[index] = true;
      }
    });
    setExpandedItems(newExpandedItems);
  }, [location.pathname, routes, isChildActive]);

  const toggleItem = (index, e) => {
    e.stopPropagation();
    if (!isCollapsed) {
      setExpandedItems((prev) => ({
        ...prev,
        [index]: !prev[index],
      }));
    }
  };

  const createLinks = (routes, level = 0, parentPath = "") => {
    return routes.map((route, index) => {
      if (route.layout !== "/admin") return null;

      const hasChildren = route.children?.length > 0;
      const fullPath = parentPath
        ? `${parentPath}/${route.path}`
        : `${route.layout}/${route.path}`;
      const isActive = activeRoute(fullPath) || isChildActive(route.children);
      const isExpanded = expandedItems[index];

      return (
        <React.Fragment key={`${level}-${index}`}>
          <div
            className={`relative ${level > 0 ? "pl-6" : ""} mb-1 ${
              isCollapsed ? "tooltip" : ""
            }`}
            data-tooltip={isCollapsed ? route.name : ""}
          >
            {hasChildren ? (
              <div
                className={`flex items-center px-4 py-2 rounded-lg hover:cursor-pointer ${
                  isActive ? "bg-lightPrimary/40 dark:bg-navy-700" : ""
                }`}
                onClick={(e) => toggleItem(index, e)}
              >
                <span
                  className={
                    isActive ? "text-brand-500 dark:text-white" : "text-gray-600"
                  }
                >
                  {route.icon || <DashIcon />}
                </span>
                {!isCollapsed && (
                  <p
                    className={`ml-3 flex-1 text-xl ${
                      isActive
                        ? "font-bold text-navy-700 dark:text-white"
                        : "font-medium text-gray-600"
                    }`}
                  >
                    {route.name}
                  </p>
                )}
                {!isCollapsed && hasChildren && (
                  <MdArrowDropDown
                    className={`h-4 w-4 text-gray-500 transition-transform ${
                      isExpanded ? "rotate-180" : ""
                    }`}
                  />
                )}
              </div>
            ) : (
              <NavLink
                to={fullPath}
                className={({ isActive }) =>
                  `flex items-center px-4 py-2 rounded-lg ${
                    isActive ? "bg-lightPrimary/40 dark:bg-navy-700" : ""
                  }`
                }
              >
                <span
                  className={
                    isActive ? "text-brand-500 dark:text-white" : "text-gray-600"
                  }
                >
                  {route.icon || <DashIcon />}
                </span>
                {!isCollapsed && (
                  <p
                    className={`ml-3 flex-1 text-xl ${
                      isActive
                        ? "font-bold text-navy-700 dark:text-white"
                        : "font-medium text-gray-600"
                    }`}
                  >
                    {route.name}
                  </p>
                )}
                {route.badge && (
                  <span className="ml-2 bg-red-500 text-white rounded-full px-2">
                    {route.badge}
                  </span>
                )}
              </NavLink>
            )}
            {isActive && !hasChildren && (
              <div className="absolute right-0 top-1/2 h-5 w-1 -translate-y-1/2 rounded-lg bg-brand-500 dark:bg-brand-400" />
            )}
          </div>

          {hasChildren && isExpanded && !isCollapsed && (
            <div className="overflow-hidden">
              {createLinks(route.children, level + 1, fullPath)}
            </div>
          )}
        </React.Fragment>
      );
    });
  };

  return (
    <div className="space-y-1">
      {createLinks(routes)}
      <style jsx>{`
        .tooltip:hover:after {
          content: attr(data-tooltip);
          position: absolute;
          left: 100%;
          top: 50%;
          transform: translateY(-50%);
          background: #333;
          color: white;
          padding: 5px 10px;
          border-radius: 4px;
          white-space: nowrap;
          z-index: 1000;
        }
      `}</style>
    </div>
  );
}

export default SidebarLinks;
